import { useEffect, useState, useCallback } from 'react';
import { getRoles, deleteRole } from '../../api/roleApi';
import { formatDate } from '../../utils/formatters';
import RoleForm from '../../components/RoleForm/RoleForm';
import RoleDetail from '../../components/RoleDetail/RoleDetail';
import './RoleManagement.css';

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [detailRole, setDetailRole] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getRoles()
      .then(setRoles)
      .catch(err => showToast(err.message, 'err'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await deleteRole(role._id);
      showToast(`Role "${role.name}" deleted.`);
      load();
    } catch (err) {
      showToast(err.message, 'err');
    }
  };

  const filtered = roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
  const totalUsers = roles.reduce((s, r) => s + (r.user_count || 0), 0);
  const protectedCount = roles.filter(r => r.is_protected).length;

  return (
    <div className="role-mgmt">
      {toast && (
        <div className={`role-mgmt__toast ${toast.type === 'err' ? 'role-mgmt__toast--error' : 'role-mgmt__toast--success'}`}>
          {toast.type === 'err' ? '\u2717 ' : '\u2713 '}{toast.msg}
        </div>
      )}

      <div className="role-mgmt__header">
        <div>
          <h1 className="role-mgmt__title">Role Management</h1>
          <p className="role-mgmt__subtitle">Define roles and configure module-level permissions for your CRM.</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowCreate(true)}>+ New Role</button>
      </div>

      <div className="role-mgmt__kpi-strip">
        <div className="role-mgmt__kpi">
          <div className="role-mgmt__kpi-val">{roles.length}</div>
          <div className="role-mgmt__kpi-lbl">Total Roles</div>
        </div>
        <div className="role-mgmt__kpi">
          <div className="role-mgmt__kpi-val">{protectedCount}</div>
          <div className="role-mgmt__kpi-lbl">Protected Roles</div>
        </div>
        <div className="role-mgmt__kpi">
          <div className="role-mgmt__kpi-val">{totalUsers}</div>
          <div className="role-mgmt__kpi-lbl">Total Users</div>
        </div>
      </div>

      <div className="role-mgmt__card">
        <div className="role-mgmt__search-wrap">
          <input
            className="role-mgmt__search"
            placeholder="Search roles\u2026"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="role-mgmt__empty">Loading roles\u2026</div>
        ) : filtered.length === 0 ? (
          <div className="role-mgmt__empty">No roles found. Create your first role.</div>
        ) : (
          <table className="role-mgmt__table">
            <thead>
              <tr>
                <th className="role-mgmt__th">Role Name</th>
                <th className="role-mgmt__th">Description</th>
                <th className="role-mgmt__th">Users</th>
                <th className="role-mgmt__th">Type</th>
                <th className="role-mgmt__th">Created</th>
                <th className="role-mgmt__th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(role => (
                <tr key={role._id} className="role-mgmt__row">
                  <td className="role-mgmt__td role-mgmt__td--name">{role.name}</td>
                  <td className="role-mgmt__td role-mgmt__td--muted">{role.description || '\u2014'}</td>
                  <td className="role-mgmt__td">{role.user_count || 0}</td>
                  <td className="role-mgmt__td">
                    <span className={`role-mgmt__badge ${role.is_protected ? 'role-mgmt__badge--protected' : 'role-mgmt__badge--custom'}`}>
                      {role.is_protected ? 'Protected' : 'Custom'}
                    </span>
                  </td>
                  <td className="role-mgmt__td role-mgmt__td--muted">{formatDate(role.created_at)}</td>
                  <td className="role-mgmt__td">
                    <button className="role-mgmt__act-btn" onClick={() => setDetailRole(role)}>View</button>
                    <button className="role-mgmt__act-btn" onClick={() => setEditRole(role)}>Edit</button>
                    {!role.is_protected && (
                      <button className="role-mgmt__del-btn" onClick={() => handleDelete(role)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <RoleForm
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); showToast('Role created successfully.'); }}
        />
      )}

      {editRole && (
        <RoleForm
          role={editRole}
          onClose={() => setEditRole(null)}
          onSaved={() => { setEditRole(null); load(); showToast('Role updated successfully.'); }}
        />
      )}

      {detailRole && (
        <RoleDetail
          role={detailRole}
          onClose={() => setDetailRole(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
